import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Table,
  Text,
  TextInput,
  ColorInput,
  LoadingOverlay,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconEdit,
  IconPlus,
  IconTrash,
  IconTrophy,
  IconSeeding,
  IconCompass,
  IconTarget,
  IconCrown,
  IconDiamond,
  IconStar,
} from "@tabler/icons-react";

import { useEffect, useState } from "react";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../hooks/usePerformRequest";
import { Level } from "../../../lib/types";
import { showNotification } from "@mantine/notifications";

const LevelsPage = () => {
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState<Level | null>(null);

  const form = useForm({
    initialValues: {
      level: 1,
      name: "",
      icon: "",
      minCoins: 0,
      maxCoins: null as number | null,
      color: "#000000",
      description: "",
      rewardMultiplier: 1.0,
    },
    validate: {
      name: (value) =>
        value.length < 2 ? "Name must be at least 2 characters" : null,
      level: (value) => (value < 1 ? "Level must be at least 1" : null),
      minCoins: (value) => (value < 0 ? "Cannot be negative" : null),
      rewardMultiplier: (value) => (value < 1 ? "Must be at least 1.0" : null),
    },
  });

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const endpoint = Endpoints.GetLevels();
      const response = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });

      const { status, data } = response;

      if (status === 200 && data) {
        // Handle both { data: [...] } and [...] formats
        const levelsData = Array.isArray(data) ? data : data.data;

        if (Array.isArray(levelsData)) {
          setLevels(levelsData);
        } else {
          console.error("Unexpected response data format:", data);
          setLevels([]);
        }
      }
    } catch (error) {
      console.error(error);
      showNotification({
        title: "Error",
        message: "Failed to fetch levels",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
    document.title = "Level Management | Virel Admin";
  }, []);

  // Predefined configuration for levels 1-6
  const LEVEL_CONFIG: Record<
    number,
    { iconName: string; Icon: any; color: string }
  > = {
    1: { iconName: "Sprout", Icon: IconSeeding, color: "#40c057" }, // Green
    2: { iconName: "Compass", Icon: IconCompass, color: "#228be6" }, // Blue
    3: { iconName: "Target", Icon: IconTarget, color: "#fd7e14" }, // Orange
    4: { iconName: "Trophy", Icon: IconTrophy, color: "#fcc419" }, // Yellow
    5: { iconName: "Crown", Icon: IconCrown, color: "#be4bdb" }, // Grape
    6: { iconName: "Diamond", Icon: IconDiamond, color: "#15aabf" }, // Cyan
  };

  const handleEdit = (level: Level) => {
    setEditingLevel(level);
    form.setValues({
      level: level.level,
      name: level.name,
      icon: level.icon,
      minCoins: level.minCoins,
      maxCoins: level.maxCoins,
      color: level.color,
      description: level.description,
      rewardMultiplier: level.rewardMultiplier,
    });
    open();
  };

  const handleSubmit = async (values: typeof form.values) => {
    setSubmitting(true);
    try {
      // Auto-assign icon and color based on level number
      const levelConfig = LEVEL_CONFIG[values.level] || {
        iconName: "Star",
        Icon: IconStar,
        color: "#adb5bd",
      };

      const payload = {
        ...values,
        icon: levelConfig.iconName,
        color: levelConfig.color,
        maxCoins: values.maxCoins || null,
      };

      const endpoint = editingLevel
        ? Endpoints.UpdateLevel(editingLevel._id)
        : Endpoints.CreateLevel();

      const { status, message } = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: payload,
      });

      if (status === 200 || status === 201) {
        showNotification({
          title: "Success",
          message: `Level ${editingLevel ? "updated" : "created"} successfully`,
          color: "green",
        });

        close();
        fetchLevels();
        form.reset();
        setEditingLevel(null);
      } else {
        throw new Error(message || "Operation failed");
      }
    } catch (error: any) {
      console.error(error);
      showNotification({
        title: "Error",
        message: error.message || "Operation failed",
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (level: Level) => {
    setLevelToDelete(level);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!levelToDelete) return;

    try {
      const endpoint = Endpoints.DeleteLevel(levelToDelete._id);
      const { status, message } = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (status === 200) {
        showNotification({
          title: "Success",
          message: "Level deleted successfully",
          color: "green",
        });
        fetchLevels();
      } else {
        throw new Error(message || "Failed to delete level");
      }
    } catch (error: any) {
      showNotification({
        title: "Error",
        message: error.message || "Failed to delete level",
        color: "red",
      });
    } finally {
      setDeleteModalOpen(false);
      setLevelToDelete(null);
    }
  };

  const handleOpen = () => {
    setEditingLevel(null);
    form.reset();
    open();
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      <Group justify="space-between" mb="lg">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Level Management</h1>
          <Text c="dimmed" size="sm">
            Manage user progression levels and rewards
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={18} />}
          onClick={handleOpen}
          color="dark"
          disabled={levels.length >= 6}
        >
          {levels.length >= 6 ? "Max Levels Reached" : "Create Level"}
        </Button>
      </Group>

      <Paper withBorder p={0} radius="md" className="overflow-hidden">
        <div className="relative">
          <LoadingOverlay visible={loading} />
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Level</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Icon</Table.Th>
                <Table.Th>Coin Range</Table.Th>
                <Table.Th>Multiplier</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {levels.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" py="xl" c="dimmed">
                      No levels found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                levels.map((level) => (
                  <Table.Tr key={level._id}>
                    <Table.Td>
                      <Badge
                        size="lg"
                        circle
                        color={level.color}
                        style={{ width: 30, height: 30 }}
                      >
                        {level.level}
                      </Badge>
                    </Table.Td>
                    <Table.Td fw={600}>{level.name}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {(() => {
                          const Icon =
                            LEVEL_CONFIG[level.level]?.Icon || IconStar;
                          return <Icon size={16} />;
                        })()}
                        <Text size="sm">{level.icon}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {level.minCoins.toLocaleString()} -{" "}
                        {level.maxCoins ? level.maxCoins.toLocaleString() : "∞"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="green">
                        x{level.rewardMultiplier.toFixed(1)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1} w={200}>
                        {level.description}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleEdit(level)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => confirmDelete(level)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      {/* Edit/Create Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={editingLevel ? "Edit Level" : "Create New Level"}
        centered
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            <NumberInput
              label="Level Number"
              placeholder="e.g. 1"
              required
              {...form.getInputProps("level")}
            />
            <TextInput
              label="Level Name"
              placeholder="e.g. Novice"
              required
              {...form.getInputProps("name")}
            />
            {/* Icon and Color inputs removed for auto-assignment */}
            <NumberInput
              label="Min Coins"
              placeholder="e.g. 0"
              required
              {...form.getInputProps("minCoins")}
            />
            <NumberInput
              label="Max Coins"
              placeholder="Leave empty for infinity"
              {...form.getInputProps("maxCoins")}
            />
            <NumberInput
              label="Reward Multiplier"
              placeholder="e.g. 1.0"
              decimalScale={1}
              step={0.1}
              min={1.0}
              required
              {...form.getInputProps("rewardMultiplier")}
            />
            <TextInput
              label="Description"
              placeholder="Level description"
              className="col-span-2"
              required
              {...form.getInputProps("description")}
            />
          </div>

          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" color="dark" loading={submitting}>
              {editingLevel ? "Update Level" : "Create Level"}
            </Button>
          </Group>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Level"
        centered
      >
        <Text size="sm">
          Are you sure you want to delete level{" "}
          <strong>{levelToDelete?.name}</strong>? This action cannot be undone.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete Level
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default LevelsPage;
